/**
 * 3D Perlin noise. The third axis is time — sampling the same (x, y) at a
 * slowly advancing z is what makes the field undulate instead of sitting still.
 *
 * This is Perlin's improved-noise formulation: gradients are looked up through a
 * shuffled permutation table rather than stored in a map. That matters here, not
 * as premature tuning — a single frame takes ~100k samples, so anything that
 * allocates or hashes per lookup shows up directly as dropped frames.
 */
export default class Perlin {

  constructor() {
    this.seed();
  }

  // Shuffle the permutation table for a brand new field.
  seed() {
    const table = new Uint8Array(256);
    for (let i = 0; i < 256; i++) table[i] = i;
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [table[i], table[j]] = [table[j], table[i]];
    }
    // Doubled so the +1 neighbour lookups never need a bounds check.
    this.perm = new Uint8Array(512);
    for (let i = 0; i < 512; i++) this.perm[i] = table[i & 255];
  }

  get(x, y, z) {
    const p = this.perm;

    // Which unit cube we are in, and where we are inside it.
    const cx = Math.floor(x) & 255;
    const cy = Math.floor(y) & 255;
    const cz = Math.floor(z) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    z -= Math.floor(z);

    const u = fade(x);
    const v = fade(y);
    const w = fade(z);

    // Hashed indices for the cube's 8 corners.
    const a = p[cx] + cy;
    const aa = p[a] + cz;
    const ab = p[a + 1] + cz;
    const b = p[cx + 1] + cy;
    const ba = p[b] + cz;
    const bb = p[b + 1] + cz;

    // Blend the 8 corner gradients along x, then y, then z.
    return lerp(w,
      lerp(v,
        lerp(u, grad(p[aa], x, y, z), grad(p[ba], x - 1, y, z)),
        lerp(u, grad(p[ab], x, y - 1, z), grad(p[bb], x - 1, y - 1, z))),
      lerp(v,
        lerp(u, grad(p[aa + 1], x, y, z - 1), grad(p[ba + 1], x - 1, y, z - 1)),
        lerp(u, grad(p[ab + 1], x, y - 1, z - 1), grad(p[bb + 1], x - 1, y - 1, z - 1))));
  }
}

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(t, a, b) {
  return a + t * (b - a);
}

// Dot the corner offset against one of 12 gradient directions, picked by hash.
function grad(hash, x, y, z) {
  const h = hash & 15;
  const u = h < 8 ? x : y;
  const v = h < 4 ? y : (h === 12 || h === 14 ? x : z);
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}
